import { KPIDirection, KPIUnit } from "../types";
import { formatValue } from "./toDashboardKPI";

export interface ThresholdBarMark {
  label: string;
  positionPercent: number;
}

export type ThresholdBarLabelAlignment = "center" | "end" | "start";

export interface ThresholdBarBoundary extends ThresholdBarMark {
  /** How the label sits against its boundary: centered on it, or ending or starting at it. */
  alignment: ThresholdBarLabelAlignment;
}

export interface ThresholdBar {
  /** CSS background, low values on the left. */
  gradient: string;
  boundaries: ThresholdBarBoundary[];
  latestValue: ThresholdBarMark | null;
}

export interface ThresholdScale {
  min: number;
  max: number;
  /** How far a handle moves per arrow key press, and the grid it snaps to. */
  step: number;
}

const PADDING_SHARE = 0.15;

/** Roughly how many steps a handle can take across the whole bar. */
const STEPS_ACROSS_SCALE = 200;

/** Below this gap, two centered labels would run into each other. */
const MIN_CENTERED_LABEL_GAP_PERCENT = 15;

const GREEN = "#22c55e";
const YELLOW = "#facc15";
const RED = "#ef4444";

const UNIT_MIN_STEPS: Record<KPIUnit, number> = {
  percent: 0.1,
  days: 1,
  ratio: 0.01,
  currency: 1,
};

/** Used when the KPI has no figures and no saved thresholds to fit. */
const FALLBACK_RANGES: Record<KPIUnit, [number, number]> = {
  percent: [0, 100],
  days: [0, 120],
  ratio: [0, 3],
  currency: [0, 100_000],
};

const countDecimals = (step: number): number =>
  step >= 1 ? 0 : Math.ceil(-Math.log10(step) - 1e-9);

/**
 * Snaps to the step grid. The quotient is trimmed first so 7 / 0.2 floors to
 * 35, not 34, and the result so 0.1 steps don't leave 12.300000001.
 */
export const snapToStep = (
  value: number,
  step: number,
  round: (value: number) => number = Math.round,
): number => {
  const stepCount = round(Number((value / step).toFixed(9)));

  return Number((stepCount * step).toFixed(countDecimals(step)));
};

/** The smallest 1, 2 or 5 × 10ⁿ that is at least `roughStep`. */
const findNiceStep = (roughStep: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const niceMultiple = [1, 2, 5, 10].find(
    (multiple) => multiple * magnitude >= roughStep,
  );

  return (niceMultiple ?? 10) * magnitude;
};

const findPaddedRange = (values: number[], unit: KPIUnit): [number, number] => {
  if (values.length === 0) {
    return FALLBACK_RANGES[unit];
  }

  const lowest = Math.min(...values);
  const highest = Math.max(...values);
  const spread = highest - lowest;
  const padding =
    spread > 0 ? spread * PADDING_SHARE : Math.max(Math.abs(highest), 1) / 2;

  return [lowest - padding, highest + padding];
};

/**
 * The bar's scale, fitted to the saved boundaries, the latest value and the
 * KPI's recent months. Worked out once per opening so it holds still while
 * the handles are dragged.
 */
export const findThresholdScale = (
  values: number[],
  unit: KPIUnit,
): ThresholdScale => {
  const [low, high] = findPaddedRange(values, unit);
  const step = Math.max(
    UNIT_MIN_STEPS[unit],
    findNiceStep((high - low) / STEPS_ACROSS_SCALE),
  );

  return {
    min: snapToStep(low, step, Math.floor),
    max: snapToStep(high, step, Math.ceil),
    step,
  };
};

const placeOnScale = (value: number, scale: ThresholdScale): number =>
  ((value - scale.min) / (scale.max - scale.min)) * 100;

/**
 * No hard edges. On target holds green for its outer half, then shades into
 * yellow at the Watch boundary; Watch shades from yellow to red at the Off
 * target boundary; Off target is solid red.
 */
const buildGradient = (
  direction: KPIDirection,
  lowPosition: number,
  highPosition: number,
): string => {
  const stops: [string, number][] =
    direction === "lower_is_better"
      ? [
          [GREEN, 0],
          [GREEN, lowPosition / 2],
          [YELLOW, lowPosition],
          [RED, highPosition],
          [RED, 100],
        ]
      : [
          [RED, 0],
          [RED, lowPosition],
          [YELLOW, highPosition],
          [GREEN, (highPosition + 100) / 2],
          [GREEN, 100],
        ];

  const css = stops.map(([color, position]) => `${color} ${position}%`);

  return `linear-gradient(to right, ${css.join(", ")})`;
};

/** Close boundaries get their labels pushed apart, outward from Watch. */
const buildBoundaries = (
  lowBoundary: number,
  highBoundary: number,
  lowPosition: number,
  highPosition: number,
  unit: KPIUnit,
): ThresholdBarBoundary[] => {
  const areLabelsCrowded =
    highPosition - lowPosition < MIN_CENTERED_LABEL_GAP_PERCENT;

  return [
    {
      label: formatValue(lowBoundary, unit),
      positionPercent: lowPosition,
      alignment: areLabelsCrowded ? "end" : "center",
    },
    {
      label: formatValue(highBoundary, unit),
      positionPercent: highPosition,
      alignment: areLabelsCrowded ? "start" : "center",
    },
  ];
};

/**
 * The thresholds as a bar on the given scale. Expects the boundaries to be
 * in order; the slider keeps its handles from crossing.
 */
export const buildThresholdBar = (
  watchStartsAt: number,
  offTargetStartsAt: number,
  unit: KPIUnit,
  direction: KPIDirection,
  latestValue: number | null,
  scale: ThresholdScale,
): ThresholdBar => {
  const lowBoundary = Math.min(watchStartsAt, offTargetStartsAt);
  const highBoundary = Math.max(watchStartsAt, offTargetStartsAt);

  const lowPosition = placeOnScale(lowBoundary, scale);
  const highPosition = placeOnScale(highBoundary, scale);

  return {
    gradient: buildGradient(direction, lowPosition, highPosition),
    boundaries: buildBoundaries(
      lowBoundary,
      highBoundary,
      lowPosition,
      highPosition,
      unit,
    ),
    latestValue:
      latestValue === null
        ? null
        : {
            label: formatValue(latestValue, unit),
            positionPercent: placeOnScale(latestValue, scale),
          },
  };
};
